import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Eye } from "lucide-react";
import { Button } from "react-bootstrap";
import { saveAs } from "file-saver";
import { RiShareForwardLine, RiDownloadFill } from "react-icons/ri";
import { MdOutlineBlock, MdOutlineDeleteOutline } from "react-icons/md";
import { HiOutlineMail } from "react-icons/hi";
import { FaArrowLeft } from "react-icons/fa6";
import { SiTicktick } from "react-icons/si";
import { ImConfused } from "react-icons/im";
import { Document, Packer, Paragraph, TextRun } from "docx";
import ApiService from "../../../nhrc_apis/ApiService";

const { JobAPI } = ApiService;

function TotalResponses() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const pageSize = 10;

  const [jobTitle, setJobTitle] = useState("Unknown Position");
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [skillFilter, setSkillFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [nameFilter, setNameFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [experienceFilter, setExperienceFilter] = useState("");
  const [page, setPage] = useState(1);

  /* ================= NORMALIZE API CANDIDATE ================= */
  const normalizeCandidate = useCallback((item, index) => {
    return {
      id: item.student_membership_id || item.id || `app-${index}`,
      applicationId:
        item.id || item.application_id || item.student_membership_id,
      studentMembershipId: item.student_membership_id || "",
      jobId: item.job_id || jobId,

      name:
        item.full_name ||
        `${item.first_name || ""} ${item.last_name || ""}`.trim() ||
        item.first_name ||
        "Unknown",
      firstName: item.first_name || "",
      lastName: item.last_name || "",

      email: item.email || "",
      phone:
        item.phone ||
        item.mobile ||
        item.contact_number ||
        item.whatsapp_number ||
        "",

      college: item.college || "",
      panNumber: item.pan_number || "",
      experienceType: item.experience_type || "",
      experience:
        item.experience ??
        item.total_experience ??
        item.years_of_experience ??
        null,

      keySkills:
        item.key_skills || item.skills || item.required_skills || "",

      panCardFile: item.pan_card_file || "",
      resumeFile: item.resume_file || item.resume || "",

      status: item.status || item.application_status || "Pending",

      raw: item,
    };
  }, [jobId]);

  /* ================= LOAD CANDIDATES FROM API ================= */
  const loadCandidates = useCallback(async () => {
    if (!jobId) {
      setDebugInfo("No jobId in URL params");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setDebugInfo(null);

      const response = await JobAPI.getHrJobApplications(jobId);
      const data = response?.data;

      const appsData = Array.isArray(data?.applications)
        ? data.applications
        : Array.isArray(data)
        ? data
        : [];

      if (data?.job_title) {
        setJobTitle(data.job_title);
      }

      const normalized = appsData.map((item, idx) =>
        normalizeCandidate(item, idx)
      );
      setCandidates(normalized);

      setDebugInfo(
        `Status: ${response?.status} | total_applications: ${data?.total_applications} | parsed: ${normalized.length}`
      );
    } catch (err) {
      const status = err?.response?.status;
      const errData = err?.response?.data;

      setDebugInfo(`ERROR Status: ${status} | ${JSON.stringify(errData)}`);

      if (status === 403) {
        setError(
          errData?.detail ||
            "Applications available only after admin approval"
        );
      } else if (status === 404) {
        setError("No applications found for this job");
      } else {
        setError("Failed to load applications");
      }
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  }, [jobId, normalizeCandidate]);

  useEffect(() => {
    loadCandidates();
  }, [loadCandidates]);

  /* ================= JOB TITLE (fallback from location.state) ================= */
  useEffect(() => {
    if (location.state?.jobTitle) {
      setJobTitle((prev) =>
        prev === "Unknown Position" ? location.state.jobTitle : prev
      );
    }
  }, [location.state]);

  /* ================= FILTER LOGIC ================= */

  const jobCandidates = candidates;

  const filteredAll = useMemo(() => {
    return jobCandidates.filter((c) => {
      if (
        statusFilter !== "all" &&
        c.status?.toLowerCase() !== statusFilter
      )
        return false;

      if (
        nameFilter &&
        !c.firstName?.toLowerCase().includes(nameFilter.toLowerCase()) &&
        !c.name?.toLowerCase().includes(nameFilter.toLowerCase())
      )
        return false;

      if (
        skillFilter &&
        !c.keySkills?.toLowerCase().includes(skillFilter.toLowerCase())
      )
        return false;

      if (
        emailFilter &&
        !c.email?.toLowerCase().includes(emailFilter.toLowerCase())
      )
        return false;

      if (
        experienceFilter &&
        !String(c.experience ?? c.experienceType ?? "")
          .toLowerCase()
          .includes(experienceFilter.toLowerCase())
      )
        return false;

      return true;
    });
  }, [
    jobCandidates,
    statusFilter,
    nameFilter,
    skillFilter,
    emailFilter,
    experienceFilter,
  ]);

  const totalPages = Math.max(1, Math.ceil(filteredAll.length / pageSize));

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredAll.slice(start, start + pageSize);
  }, [filteredAll, page]);

  const allSelected =
    paged.length > 0 && paged.every((c) => selectedIds.includes(c.id));

  /* ================= ACTIONS ================= */
  const toggleSelectAll = (e) => {
    setSelectedIds(e.target.checked ? paged.map((c) => c.id) : []);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const resetFilters = () => {
    setStatusFilter("all");
    setNameFilter("");
    setExperienceFilter("");
    setEmailFilter("");
    setSkillFilter("");
    setPage(1);
  };

  const getStatusBadgeClass = (status) => {
    const s = status?.toLowerCase();
    if (s === "shortlisted") return "border border-success text-success";
    if (s === "maybe") return "border border-warning text-warning";
    if (s === "rejected") return "border border-danger text-danger";
    return "border border-secondary text-secondary";
  };

  /* ================= STATUS CHANGE (API) ================= */
  const handleStatusChange = async (id, newStatus) => {
    const candidate = candidates.find((c) => c.id === id);
    if (!candidate) return;

    setCandidates((prev) =>
      prev.map((c) => (c.id === id ? { ...c, status: newStatus } : c))
    );
    setEditingId(null);

    try {
      if (newStatus === "Shortlisted") {
        await JobAPI.shortlistApplication(candidate.applicationId);
      } else if (newStatus === "Rejected") {
        await JobAPI.rejectApplication(candidate.applicationId);
      }
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to update status");
      loadCandidates();
    }
  };

  const bulkUpdateStatus = async (status) => {
    if (!selectedIds.length) {
      alert("Select candidates first");
      return;
    }

    const targets = candidates.filter((c) => selectedIds.includes(c.id));

    setCandidates((prev) =>
      prev.map((c) => (selectedIds.includes(c.id) ? { ...c, status } : c))
    );

    try {
      await Promise.all(
        targets.map((c) => {
          if (status === "Shortlisted") {
            return JobAPI.shortlistApplication(c.applicationId);
          } else if (status === "Rejected") {
            return JobAPI.rejectApplication(c.applicationId);
          }
          return Promise.resolve();
        })
      );
    } catch (err) {
      alert("Some updates failed, refreshing list");
      loadCandidates();
    }

    setSelectedIds([]);
  };

  const handleViewCandidate = (id) => {
    navigate(`/hrdashboard/CandidateProfile/${id}`);
  };

  const handleDelete = () => {
    if (!selectedIds.length) {
      alert("Select candidates to delete");
      return;
    }

    if (
      !window.confirm(
        `Remove ${selectedIds.length} candidate(s) from this list?`
      )
    )
      return;

    setCandidates((prev) => prev.filter((c) => !selectedIds.includes(c.id)));
    setSelectedIds([]);
    setPage(1);
  };

  const handleDownloadSelected = async () => {
    if (!selectedIds.length) {
      alert("Select at least one candidate");
      return;
    }

    const selected = candidates.filter((c) => selectedIds.includes(c.id));

    const doc = new Document({
      sections: selected.map((c) => ({
        children: [
          new Paragraph({
            children: [
              new TextRun({
                text: "Candidate Details",
                bold: true,
                size: 28,
              }),
            ],
          }),
          new Paragraph(`Name: ${c.name}`),
          new Paragraph(`Email: ${c.email}`),
          new Paragraph(`Phone: ${c.phone}`),
          new Paragraph(`College: ${c.college}`),
          new Paragraph(`Experience: ${c.experience ?? c.experienceType ?? "-"}`),
          new Paragraph(`Skills: ${c.keySkills}`),
          new Paragraph(`Status: ${c.status}`),
          new Paragraph(" "),
        ],
      })),
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, "candidates.docx");
  };

  /* ================= UI ================= */
  return (
    <div className="container-fluid py-3 mt-5">

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="justify-content-start">
          <button
            className="btn btn-outline-secondary btn-sm mb-2"
            onClick={() => navigate(-1)}
          >
            <FaArrowLeft /> Back to Jobs
          </button>
          <h5 className="mb-0">Candidates Responses</h5>
          <small className="text-muted">
            Position: <span className="fw-semibold">{jobTitle}</span>
          </small>
        </div>
        <div className="d-flex justify-content-md-end justify-content-start mt-2 mt-md-0">
          <div className="btn-toolbar" role="toolbar">
            <div className="btn-group btn-group-sm" role="group">
              <Button variant="success" size="sm" className="me-2 border rounded-3" onClick={() => bulkUpdateStatus("Shortlisted")}>
                <SiTicktick /> Shortlist
              </Button>
              <Button variant="warning" size="sm" className="me-2 border rounded-3" onClick={() => bulkUpdateStatus("Maybe")}>
                <ImConfused /> May be
              </Button>
              <Button variant="danger" size="sm" className="me-2 border rounded-3" onClick={() => bulkUpdateStatus("Rejected")}>
                <MdOutlineBlock /> Reject
              </Button>
              <Button variant="primary" size="sm" className="me-2 border rounded-3">
                <HiOutlineMail /> Email
              </Button>
              <Button variant="secondary" size="sm" className="me-2 border rounded-3">
                <RiShareForwardLine /> Forward
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                className="me-2 border rounded-3" onClick={handleDownloadSelected}
              >
                <RiDownloadFill /> Download
              </Button>
              <Button variant="outline-danger" size="sm" className="border rounded-3"
                onClick={handleDelete}>
                <MdOutlineDeleteOutline /> Delete
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* DEBUG PANEL — remove once everything works */}
      {debugInfo && (
        <div className="alert alert-info small" style={{ wordBreak: "break-all" }}>
          <strong>DEBUG:</strong> {debugInfo}
        </div>
      )}

      <div className="bg-light border rounded-3 p-3 mb-3">
        <div className="row g-3 align-items-end">
          <div className="col-md-2">
            <label className="form-label">Status</label>
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="all">All</option>
              <option value="shortlisted">Shortlisted</option>
              <option value="rejected">Rejected</option>
              <option value="maybe">Maybe</option>
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label">Candidate Name</label>
            <input
              type="text"
              className="form-control"
              placeholder="Search by name"
              value={nameFilter}
              onChange={(e) => {
                setNameFilter(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="col-md-2">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-control"
              placeholder="Search by email"
              value={emailFilter}
              onChange={(e) => {
                setEmailFilter(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">Experience</label>
            <input
              type="text"
              className="form-control"
              placeholder="Search by Experience"
              value={experienceFilter}
              onChange={(e) => {
                setExperienceFilter(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="col-md-3 d-flex justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={resetFilters}
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* LOADING */}
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
          <h6 className="mt-2 text-muted">Loading Candidates...</h6>
        </div>
      )}

      {/* ERROR / 403 (NOT YET APPROVED) */}
      {!loading && error && (
        <div className="alert alert-warning text-center" role="alert">
          {error}
        </div>
      )}

      {/* TABLE HEADER */}
      {!loading && !error && (
        <>
          <div className="border-bottom pb-2 mb-2 d-flex align-items-center">
            <div className="form-check me-3">
              <input
                type="checkbox" style={{ transform: "scale(0.8)", accentColor: "#0d6efd" }}
                className="form-check-input border-3"
                checked={allSelected}
                onChange={toggleSelectAll}
              />
            </div>
            <div className="flex-grow-1 d-flex text-muted small fw-semibold">
              <div className="col-2 text-start">Name</div>
              <div className="col-2 text-start">Email</div>
              <div className="col-2 text-start">College</div>
              <div className="col-2 text-start">Experience</div>
              <div className="col-2">Status</div>
              <div className="col-2 text-center">Actions</div>
            </div>
          </div>

          {paged.map((c) => (
            <div key={c.id} className="border-bottom py-2 d-flex align-items-center">
              <div className="form-check me-3">
                <input
                  type="checkbox" style={{ transform: "scale(0.8)", accentColor: "#0d6efd" }}
                  className="form-check-input border-3"
                  checked={selectedIds.includes(c.id)}
                  onChange={() => toggleSelect(c.id)}
                />
              </div>

              <div className="flex-grow-1 d-flex align-items-center">
                <div className="col-2 text-start">
                  <div className="fw-semibold">{c.name}</div>
                  <small className="text-muted">{c.studentMembershipId}</small>
                </div>
                <div className="col-2 text-start">
                  <small className="text-break">{c.email || "-"}</small>
                </div>
                <div className="col-2 text-start">
                  <small>{c.college || "-"}</small>
                </div>
                <div className="col-2 text-start">
                  <small>
                    {c.experience !== null
                      ? `${c.experience} Years`
                      : c.experienceType || "-"}
                  </small>
                </div>
                <div className="col-2">
                  {editingId === c.id ? (
                    <select
                      className={`form-select form-select-sm ${getStatusBadgeClass(
                        c.status
                      )}`}
                      value={c.status}
                      onChange={(e) => handleStatusChange(c.id, e.target.value)}
                      onBlur={() => setEditingId(null)}
                      autoFocus
                      style={{
                        width: "120px",
                        fontSize: "0.85rem",
                        padding: "0.35rem 0.5rem",
                      }}
                    >
                      <option value="Shortlisted">Shortlisted</option>
                      <option value="Maybe">Maybe</option>
                      <option value="Rejected">Rejected</option>
                    </select>
                  ) : (
                    <span
                      className={`badge ${getStatusBadgeClass(
                        c.status
                      )} d-inline-flex align-items-center justify-content-center`}
                      style={{
                        cursor: "pointer",
                        fontSize: "0.85rem",
                        minWidth: "120px",
                        padding: "0.35rem 0.5rem",
                      }}
                      title="Click to change status"
                      onClick={() => setEditingId(c.id)}
                    >
                      {c.status}
                    </span>
                  )}
                </div>
                <div className="col-2 text-center">
                  <button
                    className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                    title="View details"
                    onClick={() => handleViewCandidate(c.id)}
                  >
                    <Eye size={16} />
                    <span className="d-none d-md-inline">View</span>
                  </button>
                </div>
              </div>
            </div>
          ))}

          {paged.length === 0 && (
            <p className="text-muted mt-3">No candidates match the filters.</p>
          )}

          {/* Pagination */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <small className="text-muted">
              Showing {filteredAll.length === 0 ? 0 : (page - 1) * pageSize + 1}-
              {Math.min(page * pageSize, filteredAll.length)} of {filteredAll.length} responses
            </small>
            <div className="btn-group">
              <button
                className="btn btn-sm border rounded-2 m-2 btn-primary"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <small className="text-muted m-3">
                Page {page} of {Math.max(1, totalPages)}
              </small>
              <button
                className="btn btn-sm border m-2 rounded-2 btn-primary"
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default TotalResponses;